import type { IAdapter } from '@laam/events';
import {
	EventClient,
	GtagAdapter,
	GtmAdapter,
	MoengageAdapter,
	OmnisendAdapter,
	SavyourAdapter,
	StatsigAdapter,
	UpfluenceAdapter,
} from '@laam/events';
import { MailchimpAdapter } from '@laam/events/src/adapters/mailchimp';
import { MetaPixelAdapter } from '@laam/events/src/adapters/metapixel.ts';
import { defineNuxtPlugin } from 'nuxt/app';
import { useCountryCode } from '~/stores/country';
import { useGlobalStore } from '~/stores/global';
import type { InboundMarketing } from '~/types/store';
import { getCookie } from '~/utils/cookie';
export default defineNuxtPlugin(async () => {
	const { store, addOns } = useGlobalStore();
	const inBoundMarketing = store.inbound_marketing as InboundMarketing | null;
	const config = useRuntimeConfig();
	const validAdapters: IAdapter[] = [];
	const { countryCode } = useCountryCode();

	const statsig = new StatsigAdapter();
	const user = localStorage.getItem('logan')
		? JSON.parse(localStorage.getItem('logan')!)
		: null;

	// Reset stable ID if their stable id is empty due to the bug we had
	const stableId = getCookie('statsig-stable-id');

	await statsig.init({
		sdkKey: config.public.statsigSdkKey,
		environment: config.public.statsigEnvironment,
		countryCode: countryCode,
		user: {
			userID: user ? (user.isSignedIn ? user.loganId : null) : null,
			email: user ? (user.isSignedIn ? user.email : null) : null,
		},
		stableId: stableId,
	});

	validAdapters.push(statsig);

	if (inBoundMarketing?.ga) {
		const ga = new GtagAdapter();
		// TODO: rm after veryfing
		// await ga.init({
		// 	tagId: inBoundMarketing.ga.ga_measurement_id,
		// });
		validAdapters.push(ga);
	}

	if (inBoundMarketing?.omnisend) {
		const omnisend = new OmnisendAdapter();
		await omnisend.init({
			brandId: inBoundMarketing.omnisend?.brand_id,
		});
		validAdapters.push(omnisend);
	}

	if (inBoundMarketing?.moengage) {
		const moengage = new MoengageAdapter();
		await moengage.init({
			apiKey: inBoundMarketing.moengage.api_key,
			cluster: inBoundMarketing.moengage.cluster,
			debugLogs: inBoundMarketing.moengage.environment === 'development',
		});
		validAdapters.push(moengage);
	}

	let isLaamMetaPixelEnabled = true;
	if (
		addOns?.['laam-meta-pixel'] &&
		addOns['laam-meta-pixel'].enabled === false
	) {
		isLaamMetaPixelEnabled = false;
	}

	const metaPixel = new MetaPixelAdapter();
	const pixelIds = isLaamMetaPixelEnabled ? ['2173897539375580'] : [];
	if (inBoundMarketing?.meta_pixel) {
		pixelIds.push(inBoundMarketing.meta_pixel.meta_pixel_id);
	}
	metaPixel.init({
		pixelId: pixelIds,
	});
	validAdapters.push(metaPixel);

	// Savyour Adapter
	if (config.public.savyourAuthKey) {
		const savyour = new SavyourAdapter();
		savyour.init({
			authKey: config.public.savyourAuthKey,
		});
		validAdapters.push(savyour);
	}

	if (inBoundMarketing?.gtm?.container_id) {
		const gtm = new GtmAdapter();
		await gtm.init({
			containerId: inBoundMarketing.gtm.container_id,
		});
		validAdapters.push(gtm);
	}

	if (inBoundMarketing?.upfluence?.id) {
		const upf = new UpfluenceAdapter();
		await upf.init();
		validAdapters.push(upf);
	}

	// Mailchimp Adapter
	if (inBoundMarketing?.mailchimp) {
		const mailchimp = new MailchimpAdapter();
		await mailchimp.init({
			baseUrl: config.public.medusaUrl as string,
			storeId: store.id,
			email: user?.email,
		});
		validAdapters.push(mailchimp);
	}

	const eventClient = new EventClient();
	eventClient.setAdapters(validAdapters);

	return {
		provide: {
			eventClient,
		},
	};
});

declare module 'nuxt/app' {
	interface NuxtApp {
		$eventClient: EventClient;
	}
}
